import {
  Select,
  SelectVariant,
  Text,
  TextSize,
  TextWeight,
  makeStyles
} from '@focaldata/cin-ui-components'
import { ReactNode } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { canAuthorMultiMarket } from '../../../../constants/featureFlags'
import { AnalyticsFeatureFlagProvider } from '../../../../contexts/AnalyticsFeatureFlagContext'
import { SurveyState, Wave } from '../../../../data/gql-gen/dashboard/graphql'
import { useProjectId } from '../../../../hooks/useProjectId'
import { AudienceContextProvider } from '../../../../modules/Audience/Audience.context'
import { useGetProjectByProjectId } from '../../../../modules/Project/Project.hooks'
import { useProjectMarkets } from '../../../../modules/Questionnaire/MarketVisibility/useProjectMarkets'
import { QuestionnaireContextProvider } from '../../../../modules/Questionnaire/Questionnaire.context'
import Results from '../../Survey/Results/Results'
import { ProjectFlowBreadcrumb } from '../ProjectFlowBreadcrumb'
import { getMarkets, surveysByCreation } from '../markets.utils'
import { CombinedResults } from './CombinedResults'

const useStyles = makeStyles()({
  // Fixed-height column: breadcrumb + header stay put, the results area fills the
  // rest and scrolls internally — the page itself never scrolls.
  outer: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 16,
    flexWrap: 'wrap',
    padding: '24px 32px 16px',
    flexShrink: 0
  },
  controls: { display: 'flex', alignItems: 'center', gap: 12 },
  toggle: { display: 'inline-flex', gap: 8 },
  toggleButton: {
    border: '1px solid #E5EAF5',
    borderRadius: 8,
    background: '#FFFFFF',
    padding: '8px 16px',
    fontWeight: 600,
    fontSize: 14,
    color: '#42526E',
    cursor: 'pointer'
  },
  toggleButtonActive: {
    background: 'var(--primary)',
    borderColor: 'var(--primary)',
    color: '#FFFFFF'
  },
  toggleButtonDisabled: { opacity: 0.5, cursor: 'not-allowed' },
  marketSelect: { minWidth: 140 },
  // The reused Results renders ResultsPageLayout, whose root hardcodes
  // height: calc(100vh - 49px) (it assumes it sits directly under one 49px
  // subheader). Here it sits under the breadcrumb + this header, so override the
  // root to fill this flex cell instead and let its inner panels scroll —
  // otherwise it overflows the viewport and leaves white space on scroll.
  resultsArea: {
    flex: 1,
    minHeight: 0,
    '& > .MuiGrid-container': { height: '100%' }
  },
  placeholder: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px 32px',
    color: '#6B7280',
    textAlign: 'center'
  }
})

/**
 * Multi-market Results. Two views selected via the header toggle:
 * - Combined (`/project/:projectId/results`): charts pooled across all markets,
 *   fetched from fd-core-responses' combined endpoint by project id (see
 *   CombinedResults).
 * - Per Market (`/project/:projectId/results/:surveyId`): the same charts as the
 *   single-market results page, scoped to the picked market. Only fielding
 *   (non-Draft) markets have results, so the selector lists those.
 */
export const ResultsPage = ({
  wave,
  breadcrumb,
  waveSelector
}: {
  wave?: Wave
  breadcrumb?: ReactNode
  waveSelector?: ReactNode
}) => {
  const { classes, cx } = useStyles()
  const projectId = useProjectId()
  const { surveyId } = useParams()
  const navigate = useNavigate()
  const { project, loading } = useGetProjectByProjectId()
  const resultsRoot = wave
    ? `/project/${projectId}/wave/${wave.waveId}/results`
    : `/project/${projectId}/results`
  const flowBreadcrumb = breadcrumb ?? (
    <ProjectFlowBreadcrumb activeStep="Results" />
  )
  const scopeReady = Boolean(wave || (!loading && project))

  // Results only exist once a market is fielding; master (oldest) first.
  const fieldingSurveyIds = new Set(
    (wave ? surveysByCreation(wave.surveys) : getMarkets(project))
      .filter((market) => market.state !== SurveyState.Draft)
      .map((market) => market.surveyId)
  )
  const marketOptions = useProjectMarkets().filter((market) =>
    fieldingSurveyIds.has(market.surveyId)
  )

  const noResultsYet = (
    <div className={classes.placeholder}>
      <Text size={TextSize.ms}>
        No fielding markets yet — results appear once a market goes live.
      </Text>
    </div>
  )

  // No results exist before any market launches; the breadcrumb hides this
  // step pre-launch, so anything arriving here (deep link, stale bookmark) is
  // sent back to the dashboard — except results-only viewers, whose dashboard
  // redirects here, so the pair would ping-pong. Waves skip the redirect: their
  // breadcrumb always shows Results and the wave picker can select a draft
  // wave, so it shows the empty state with the picker to switch back.
  if (scopeReady && fieldingSurveyIds.size === 0) {
    if (!wave && canAuthorMultiMarket()) {
      return <Navigate to={`/project/${projectId}`} replace />
    }
    return (
      <div className={classes.outer}>
        {flowBreadcrumb}
        <div className={classes.header}>
          <Text size={TextSize.xl} weight={TextWeight.Bold}>
            Results
          </Text>
          {waveSelector}
        </div>
        {noResultsYet}
      </div>
    )
  }

  // Reject a :surveyId outside the current market set. A wave's markets are
  // authoritative immediately; otherwise wait for projectById so a valid deep
  // link is not replaced by Combined while the project is still loading.
  if (scopeReady && surveyId && !fieldingSurveyIds.has(surveyId)) {
    return <Navigate to={resultsRoot} replace />
  }

  const isCombined = !surveyId
  const firstMarketId = marketOptions[0]?.surveyId
  // The market rendered in per-market mode: the route's, else the first fielding one.
  const selectedSurveyId = surveyId ?? firstMarketId

  return (
    <div className={classes.outer}>
      {flowBreadcrumb}
      <div className={classes.header}>
        <Text size={TextSize.xl} weight={TextWeight.Bold}>
          Results
        </Text>

        <div className={classes.controls}>
          {waveSelector}
          <div className={classes.toggle}>
            <button
              type="button"
              className={cx(
                classes.toggleButton,
                isCombined && classes.toggleButtonActive
              )}
              onClick={() => navigate(resultsRoot)}
            >
              Combined
            </button>
            <button
              type="button"
              disabled={!firstMarketId}
              className={cx(
                classes.toggleButton,
                !isCombined && classes.toggleButtonActive,
                !firstMarketId && classes.toggleButtonDisabled
              )}
              onClick={() =>
                selectedSurveyId &&
                navigate(`${resultsRoot}/${selectedSurveyId}`)
              }
            >
              Per Market
            </button>
          </div>

          {!isCombined && marketOptions.length > 0 && (
            <div className={classes.marketSelect}>
              <Select
                variant={SelectVariant.Outlined}
                options={marketOptions.map((market) => ({
                  id: market.surveyId,
                  name: market.flag
                    ? `${market.flag} ${market.label}`
                    : market.label,
                  value: market.surveyId
                }))}
                optionValue={selectedSurveyId}
                ariaLabel="Results market"
                onChange={(e) => {
                  const marketId = e.target.value as string
                  navigate(`${resultsRoot}/${marketId}`)
                }}
              />
            </div>
          )}
        </div>
      </div>

      {isCombined ? (
        // Combined view is project-scoped (no surveyId): it fetches pooled
        // results by projectId and reuses the same chart list as per-market.
        // Wrapped in resultsArea so the layout override (see styles) applies.
        <div className={classes.resultsArea}>
          <CombinedResults wave={wave} />
        </div>
      ) : selectedSurveyId ? (
        // Reuse the single-market results view, scoped to the selected market.
        // The route carries :surveyId, so Results' useSurveyId() resolves to it.
        // Keyed by market so switching remounts the subtree — Results holds
        // pagination/filter/quota state internally, and carrying page 2 of one
        // market into another can land past its last page (empty results).
        <div className={classes.resultsArea}>
          <AnalyticsFeatureFlagProvider
            key={selectedSurveyId}
            surveyId={selectedSurveyId}
          >
            <AudienceContextProvider>
              <QuestionnaireContextProvider>
                <Results multiMarketV2 />
              </QuestionnaireContextProvider>
            </AudienceContextProvider>
          </AnalyticsFeatureFlagProvider>
        </div>
      ) : (
        noResultsYet
      )}
    </div>
  )
}
